'use client';

import type { CountryCode } from '@inkitt/geo';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validateCoupon } from '../actions/validate-coupon';

type ApplyCodeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onApply: (code: string) => void;
  countryCode: CountryCode;
};

export const ApplyCodeModal: React.FC<ApplyCodeModalProps> = ({ isOpen, onClose, onApply, countryCode }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim()) {
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const result = await validateCoupon(code.trim(), countryCode);

      if (result.success) {
        onApply(code.trim());
        setCode('');
        onClose();
      } else {
        setError(t('subscription:invalid_promo_code', 'Invalid promo code. Please try again.'));
      }
    } catch {
      setError(t('subscription:promo_code_error', 'Unable to validate promo code. Please try again.'));
    } finally {
      setIsValidating(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#363637] rounded-xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 hover:bg-black/20 rounded-full transition-colors"
          aria-label={String(t('common:close', 'Close'))}
        >
          <X className="w-5 h-5 text-white" />
        </button>

        <h2 className="text-xl font-bold text-white mb-6">
          {t('subscription:enter_promo_code_placeholder', 'Enter Promo Code')}
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError(null);
              }}
              placeholder={String(t('subscription:enter_code_placeholder', 'Enter code'))}
              className="w-full px-4 py-3 bg-[#2a2a32] border border-[#555555] rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:border-[#66BE9E]"
              disabled={isValidating}
            />
            {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
          </div>

          <button
            type="submit"
            disabled={isValidating || !code.trim()}
            className="w-full py-3 bg-[#66BE9E] hover:bg-[#5aad8e] disabled:bg-neutral-600 disabled:cursor-not-allowed text-white font-bold rounded-lg transition-colors"
          >
            {isValidating ? t('common:loading', 'Loading...') : t('subscription:apply_promo_code', 'Apply Promo Code')}
          </button>
        </form>
      </div>
    </div>
  );
};
